import { debugLog } from "./utils/debugLog.js";
import { useEffect, useState } from 'react';
import Airtable from 'airtable';

const apiKey = process.env.REACT_APP_AIR_KEY;
const baseId = process.env.REACT_APP_AIR_BASEID;
const base = new Airtable({ apiKey: apiKey }).base(baseId);

// Cached for the session so repeat visits are free. The shared grants state is
// not a safe substitute: other pages populate it with a company-matched subset,
// and reusing that would silently shrink the catalog.
let fullGrantsCatalog = null;
let fullGrantsCatalogRequest = null;
const catalogListeners = new Set();

function emitCatalog(records, done) {
  catalogListeners.forEach((listener) => {
    try {
      listener(records, done);
    } catch (err) {
      console.error('[useGrantsWithInitialLoad] listener failed', err);
    }
  });
}

function fetchFullGrantsCatalog() {
  if (fullGrantsCatalog) return Promise.resolve(fullGrantsCatalog);
  if (!fullGrantsCatalogRequest) {
    fullGrantsCatalogRequest = new Promise((resolve, reject) => {
      const records = [];
      base('deduped_grants')
        .select({ pageSize: 100 })
        .eachPage(
          (pageRecords, fetchNextPage) => {
            records.push(...pageRecords);
            // First page only. Later pages wait for the finished catalog so the
            // finder does not rebuild search 20+ times while Airtable paginates.
            if (records.length <= pageRecords.length) {
              emitCatalog(records.slice(), false);
            }
            fetchNextPage();
          },
          (err) => {
            if (err) {
              fullGrantsCatalogRequest = null;
              reject(err);
              return;
            }
            fullGrantsCatalog = records;
            debugLog('[useGrantsWithInitialLoad] Fetched', fullGrantsCatalog.length, 'grants');
            emitCatalog(fullGrantsCatalog, true);
            fullGrantsCatalogRequest = null;
            resolve(fullGrantsCatalog);
          }
        );
    });
  }

  return fullGrantsCatalogRequest;
}

/**
 * Loads the complete deduped_grants catalog into the shared grants state.
 * Use for the grant finder, which must always see every grant.
 */
export function useGrantsWithInitialLoad(setGrants) {
  const [loading, setLoading] = useState(!fullGrantsCatalog);
  const [error, setError] = useState(null);

  useEffect(() => {
    let ignore = false;

    if (fullGrantsCatalog) {
      setGrants(fullGrantsCatalog);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError(null);

    const onCatalog = (records, done) => {
      if (ignore || !records?.length) return;
      if (!done && records.length > 100) return;
      setGrants(records);
      setLoading(false);
    };
    catalogListeners.add(onCatalog);

    fetchFullGrantsCatalog()
      .then((records) => {
        if (ignore) return;
        setGrants(records);
      })
      .catch((err) => {
        if (ignore) return;
        setError(err);
        console.error('[useGrantsWithInitialLoad] Error:', err);
      })
      .finally(() => {
        catalogListeners.delete(onCatalog);
        if (!ignore) setLoading(false);
      });

    return () => {
      ignore = true;
      catalogListeners.delete(onCatalog);
    };
  }, [setGrants]);

  return { loading, error };
}

// tableName -- What table do you want to fetch from
// callbackFn --- what do you want to with the fetched data?
//      Note : if you don't want to do anything special with the fetched data, you can leave it empty and just use the data property.
function useBase(tableName, callbackFn = null, grants = [], { enabled = true } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let ignore = false;
    setLoading(true);

    (() => {
      if (ignore) {
        debugLog('ignoring');
        return;
      }

      if (grants.length === 0) {
        base(tableName)
          .select({ view: 'Grid view' })
          .all()
          .then((data) => {
            if (callbackFn) callbackFn(data);
            setData(data);
          })
          .catch((e) => {
            setError(e);
            console.error(`Error occurred fetching from ${tableName}.`, e);
          })
          .finally(() => setLoading(false));
      }
    })();

    return () => {
      ignore = true;
    };
  }, [tableName, callbackFn, enabled]);

  return { loading, data, error };
}

export default useBase;
